import type { TrainingAttempt } from "@/lib/api/training-simulator/training-simulator.ts";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events.ts";
import { Button } from "@somethings-ui/web";
import { useState } from "react";
import { LevelInfo } from "../common/level-info.tsx";
import { TrainingLeadMentorModal } from "./training-lead-mentor-modal.tsx";
import { getModuleTheme } from "@/lib/training-simulator/module-theme.ts";

interface TrainingSessionTopbarProps {
  attempt: TrainingAttempt;
  mentorMessageCount: number;
  onEndSession: () => void;
  isEnding: boolean;
}

export function TrainingSessionTopbar({
  attempt,
  mentorMessageCount,
  onEndSession,
  isEnding,
}: TrainingSessionTopbarProps) {
  const [leadMentorOpen, setLeadMentorOpen] = useState(false);
  const { track } = useTrackEvent();

  const { level } = attempt;
  const target = level.completion_policy.target;
  const progress = Math.min(mentorMessageCount, target);
  const percent = target > 0 ? (progress / target) * 100 : 0;

  const theme = getModuleTheme(level.module);
  return (
    <header className="flex items-center justify-between gap-4 border-b border-surfaces-divider bg-surfaces-z3 px-6 py-4">
      <LevelInfo level={level} accentClassName={theme.headerClassName} />

      <div className="flex flex-col items-center gap-2">
        <span className="text-body-3 text-text-secondary">
          {progress} / {target} messages
        </span>
        <div className="h-1 w-64 overflow-hidden rounded-full bg-surfaces-z5">
          <div
            className="h-full rounded-full bg-brand-forest transition-all"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="md"
          fullWidth={false}
          onClick={() => {
            track(PostHogEvents.SIMULATOR_LEAD_MENTOR_MESSAGE_MODAL_OPEN, {
              source: "active_session",
              module_index: level.module.position,
              level_index: level.position,
            });
            setLeadMentorOpen(true);
          }}
        >
          Message Lead Mentor
        </Button>
        <Button
          variant="primary"
          size="md"
          fullWidth={false}
          onClick={onEndSession}
          loading={isEnding}
        >
          End session
        </Button>
      </div>

      <TrainingLeadMentorModal
        open={leadMentorOpen}
        onOpenChange={setLeadMentorOpen}
        attemptId={attempt.id}
      />
    </header>
  );
}
